import { getSidebarItem } from "../../../config/sidebarConfig";
import { noticesQuery } from "../../../services/queries";
import { viewQuery } from "../scopedQuery";
import { mapNoticesToCalendarEventCores } from "../../../utils/noticeCalendarEvents";
import { dayLabel, overlapsRange } from "../dateRange";
import { askedHow } from "./askedHow";
import { openPageEntry } from "./openPage";
import { loadCircles, pickCircle } from "./pickCircle";
import { AnswerContext, defineEntry, HandoffEntry, isSlotQuestion } from "./types";
import { capitalize, countOf, datedList, Dated, isAre } from "./wording";

const calendarPage = getSidebarItem("calendar");

const NO_CIRCLES = "You don't have any Circles yet, so there are no Events to show by Circle.";

const events = (n: number) => countOf(n, "Event");

/** Each Event as the Calendar shows it, from the same query and mapping, soonest first. */
async function loadEvents({ user, token, queryClient }: AnswerContext) {
  const notices = await queryClient.fetchQuery(viewQuery(user, noticesQuery(user?.schoolId, token)));
  return mapNoticesToCalendarEventCores(notices).sort((a, b) => a.start.getTime() - b.start.getTime());
}

type CalendarEvent = Awaited<ReturnType<typeof loadEvents>>[number];

/** "Friday, October 9", with the time when it has one */
const whenOf = ({ start, allDay }: CalendarEvent) =>
  allDay
    ? dayLabel(start)
    : `${dayLabel(start)} at ${start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;

const dated = (event: CalendarEvent): Dated => ({ title: event.fullTitle, when: whenOf(event) });

const openCalendar = (date?: Date) => ({
  label: `Open ${calendarPage.label}`,
  href: calendarPage.path,
  ...(date ? { state: { date } } : {}),
});

/**
 * "Create an event", "put sports day on the calendar": a guided Event, asking for the
 * title, message, day, start time, place and who it's for, then the wizard on Review.
 */
const createEventEntry: HandoffEntry = {
  id: "calendar.createEvent",
  sidebarItem: "calendar",
  guided: () => "event",
  examples: [
    "Create an event on Friday",
    "Create an event",
    "Can you create an event for me?",
    "Add an event",
    "Add an event to the calendar",
    "New event",
    "Schedule an event",
    "Put an event on the calendar",
    "Put sports day on the calendar",
    "Plan an event",
    "Set up an event",
    "Add a date to the calendar",
    "Create a staff meeting",
    "Add a staff meeting",
    "Make a calendar event",
    ...askedHow("create the clean-up as an event"),
    "calendar event for the same thing",
    "not the notice. the event.",
  ],
};

export const calendarEntries = [
  openPageEntry("calendar", "It's where you manage events and important dates.", [
    "Where can I see upcoming events?",
    "Where are our events?",
    "Show the calendar",
    "Just the calendar please",
    "Calendar only please",
    ...askedHow("open the calendar"),
    "If the date in the answer is wrong I'll check the calendar myself",
    "I'll open the calendar and check",
  ]),

  // The Calendar over a day or a week
  defineEntry<{ list: Dated[] }>({
    id: "calendar.whatsOn",
    sidebarItem: "calendar",
    examples: [
      "What's on this week?",
      "Any events this weekend?",
      "What's on this weekend?",
      "Friday events?",
      "Anything on the 28th?",
      "What's happening on 28 October?",
      "What's on in November?",
      "Do we have anything on Friday?",
      "What's on next week?",
      "What's on Friday?",
      "What's on today?",
      "Is anything on tomorrow?",
      "What's happening this week?",
      "whats going on",
      "what's happening",
      "What events are on this week?",
      "What's on the calendar next week?",
      "Which events are on Monday?",
      "Do we have anything on this week?",
      "What's coming up next week?",
      "Where do I find the event again?",
      "Is there an event on Friday?",
      "What's in my calendar?",
      "What's on my calendar this week?",
      "What's in the calendar?",
      "anything coming up",
      "show upcoming events",
      "I won't trust a date this chat invented",
    ],
    slots: { dateRange: "thisWeek" },
    dates: "ahead",
    answer: {
      load: async ({ dateRange }, ctx) => {
        const all = await loadEvents(ctx);
        return { list: all.filter((e) => dateRange && overlapsRange(e.start, e.end, dateRange) && (dateRange.end <= new Date() || e.end > new Date())).map(dated) };
      },
      templates: [
        ({ list }, { dateRange }) =>
          list.length === 0
            ? `There's nothing on the Calendar ${dateRange?.label}.`
            : `There ${isAre(list.length)} ${events(list.length)} ${dateRange?.label}: ${datedList(list)}.`,
        ({ list }, { dateRange }) =>
          list.length === 0
            ? `Nothing is on ${dateRange?.label}.`
            : `You have ${events(list.length)} ${dateRange?.label}: ${datedList(list)}.`,
        ({ list }, { dateRange }) =>
          list.length === 0
            ? `${capitalize(dateRange?.label ?? "")}, the Calendar is clear; there are no Events on it.`
            : `${capitalize(dateRange?.label ?? "")}, the Calendar shows ${events(list.length)}: ${datedList(list)}.`,
      ],
      link: ({ dateRange }) => openCalendar(dateRange?.start),
    },
  }),

  // The Calendar's coming Events for one Circle
  defineEntry<{ circle: string | null; list: Dated[] }>({
    id: "calendar.forCircle",
    sidebarItem: "calendar",
    examples: [
      "Which events are for Grade 2?",
      "What events does a circle have?",
      "Events for a class",
      "What's coming up for the class?",
      "Are there any events for the circle?",
      "Which events are for my class?",
      "List the events for a group",
      "What's on for the circle?",
      "Show me the circle's events",
      "Upcoming events for a class",
      "Does the class have anything coming up?",
      "Which events are for one circle?",
    ],
    slots: { circle: "" },
    answer: {
      load: async ({ circle: said = "" }, ctx) => {
        const [all, circles] = await Promise.all([loadEvents(ctx), loadCircles(ctx)]);
        if (circles.length === 0) return { circle: null, list: [] };
        const pick = pickCircle(circles, said);
        if (isSlotQuestion(pick)) return pick;
        const { circleId, name } = pick.circle;
        const now = new Date();
        const list = all.filter((e) => e.notice.targetSubcircle === circleId && e.end > now).map(dated);
        return { circle: name, list };
      },
      templates: [
        ({ circle, list }) =>
          !circle
            ? NO_CIRCLES
            : list.length === 0
              ? `${circle} has no Events coming up.`
              : `${circle} has ${events(list.length)} coming up: ${datedList(list)}.`,
        ({ circle, list }) =>
          !circle
            ? NO_CIRCLES
            : list.length === 0
              ? `There are no Events coming up for ${circle}.`
              : `Coming up for ${circle}: ${datedList(list)}.`,
        ({ circle, list }) =>
          !circle
            ? NO_CIRCLES
            : list.length === 0
              ? `Nothing is on the Calendar for ${circle} yet.`
              : `The Calendar shows ${events(list.length)} for ${circle}: ${datedList(list)}.`,
      ],
      link: () => openCalendar(),
    },
  }),

  defineEntry({
    id: "calendar.monthHowTo",
    sidebarItem: "calendar",
    examples: [
      "How do I see this month's calendar?",
      "Open the calendar",
      "Show me the month view",
      "Where is the diary?",
      "How do I see the month?",
      "Show me this month",
      "Where is the calendar month?",
      "How do I open the month grid?",
      "I want the monthly calendar",
      "Show October on the calendar",
      "TODAY jumps to today",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "Open Calendar in the sidebar. It shows the month, with TODAY and an Upcoming Activity list.",
        () => "Calendar opens on the month. TODAY jumps to today, and Upcoming Activity lists what's next.",
        () => "The month view is the Calendar page. ADD EVENT is the button there.",
      ],
      link: () => openCalendar(),
    },
  }),

  defineEntry({
    id: "calendar.addEventHowTo",
    sidebarItem: "calendar",
    examples: [
      "Where is the ADD EVENT button?",
      "Where is add event?",
      "I can't find ADD EVENT",
      "Where is the button to add an event?",
      "How do I find ADD EVENT?",
      "Where do I press ADD EVENT?",
      "I can't see the add event button",
      "Which page has ADD EVENT?",
      "Where is the new event button?",
      "How do I get to ADD EVENT?",
      "add event button",
      "button to put a date on",
      "ADD EVENT is on Calendar, not on Home",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "On Calendar, the button is ADD EVENT. Create also has Create an event.",
        () => "ADD EVENT is on the Calendar page. You can also press Create an event on Create.",
        () => "Open Calendar and press ADD EVENT.",
      ],
      link: () => openCalendar(),
    },
  }),

  createEventEntry,
];
